"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowDown, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function AnimatedNumber({
  value,
  prefix = "",
  suffix = "",
  duration = 2000
}: {
  value: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
}) {
  const [display, setDisplay] = useState(0);
  const containerRef = useRef<HTMLSpanElement | null>(null);
  const startedRef = useRef(false);
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting && !startedRef.current) {
        startedRef.current = true;
        const startTime = performance.now();
        const animate = (now: number) => {
          const elapsed = now - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setDisplay(Math.floor(eased * value));
          if (progress < 1) requestAnimationFrame(animate);else setDisplay(value);
        };
        requestAnimationFrame(animate);
        observer.disconnect();
      }
    }, {
      threshold: 0.3
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [value, duration]);
  return <span ref={containerRef}>
      {prefix}
      {display.toLocaleString()}
      {suffix}
    </span>;
}
export default function Seo() {
  const stats = [{
    value: 45,
    prefix: "+",
    suffix: "%",
    label: "Mehr Sichtbarkeit",
    description: "Durch gezielte Optimierung Ihrer Online-Präsenz",
    accent: "underline"
  }, {
    value: 3,
    prefix: "",
    suffix: "x",
    label: "Mehr Anfragen",
    description: "Durch bessere Platzierungen bei Google",
    accent: "sparkle"
  }, {
    value: 90,
    prefix: "",
    suffix: "%",
    label: "Lokale Relevanz",
    description: "Wir bringen Sie in Oldenburg nach vorne",
    accent: "underline"
  }, {
    value: 100,
    prefix: "",
    suffix: "%",
    label: "Technik-Fokus",
    description: "Optimierte Ladezeiten für zufriedene Kunden",
    accent: "sparkle"
  }];
  const headerReveal = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.2
  });
  const taglineReveal = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.3
  });
  return <section data-section-id="770" id="seo-betreuung" className="bg-background text-foreground relative overflow-hidden py-20 md:py-28 lg:py-36">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute -top-32 -right-40 h-[500px] w-[500px] rounded-lg opacity-20 blur-3xl" style={{
      background: "radial-gradient(circle at center, var(--primary) 0%, transparent 70%)"
    }} />

      <div className="relative mx-auto max-w-7xl px-6 md:px-10">
        {}
        <div ref={headerReveal.ref} className={`mb-14 md:mb-20 transition-all duration-1000 ease-out ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex items-center gap-3 mb-6">
            <span className="font-mono text-xs md:text-sm uppercase tracking-[0.25em] font-medium" style={{
            color: "var(--primary)"
          }}>
              SEO & Performance
            </span>
            <span className="h-px w-12 md:w-20" style={{
            backgroundColor: "var(--primary)",
            opacity: 0.5
          }} />
          </div>

          <h2 className="font-default font-light text-foreground text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl" style={{
          fontFamily: "var(--font-serif)"
        }}>
            Werden Sie gefunden, statt{" "}
            <span className="relative inline-block italic">
              nur zu existieren
              <svg aria-hidden="true" viewBox="0 0 200 12" className="absolute -bottom-2 left-0 w-full h-3" preserveAspectRatio="none">
                <path d="M2 8 Q 50 2, 100 7 T 198 5" fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </span>{" "}
            im Netz.
          </h2>
        </div>

        <Separator className="mb-14 md:mb-20 bg-border opacity-60" />

        {}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-14 md:gap-x-12 md:gap-y-16 lg:gap-12">
          {stats.map((stat, i) => {
          return <StatBlock key={i} data-index={i} index={i} value={stat.value} prefix={stat.prefix} suffix={stat.suffix} label={stat.label} description={stat.description} accent={stat.accent} />;
        })}
        </div>

        {}
        <div ref={taglineReveal.ref} className={`mt-20 md:mt-28 text-center transition-all duration-1000 ease-out ${taglineReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-default italic text-foreground text-2xl sm:text-3xl md:text-4xl leading-snug max-w-3xl mx-auto" style={{
          fontFamily: "var(--font-serif)"
        }}>
            Wir optimieren Ihre Website für nachhaltigen Erfolg in Oldenburg und darüber hinaus.
          </p>

          <Link to="/ContactPage" className="inline-flex items-center gap-2 mt-8 font-mono text-sm uppercase tracking-wider font-medium transition-all hover:gap-3" style={{
          color: "var(--primary)"
        }}>
            Jetzt Ersteinschätzung anfragen
            <ArrowDown className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>;
}
function StatBlock({
  index,
  value,
  prefix,
  suffix,
  label,
  description,
  accent,
  ...rest
}: {
  index: number;
  value: number;
  prefix: string;
  suffix: string;
  label: string;
  description: string;
  accent: string;
  [key: string]: any;
}) {
  const reveal = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.2
  });
  return <div ref={reveal.ref} className={`flex flex-col transition-all duration-1000 ease-out ${reveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
    transitionDelay: `${index * 120}ms`
  }} {...rest}>
      <div className="relative inline-flex items-start mb-4">
        <span className="font-default font-light text-foreground text-5xl sm:text-6xl md:text-7xl lg:text-[6rem] leading-none tracking-tight" style={{
        fontFamily: "var(--font-serif)"
      }}>
          <AnimatedNumber value={value} prefix={prefix} suffix={suffix} />
        </span>

        {accent === "sparkle" && <Sparkles className="h-4 w-4 md:h-5 md:w-5 ml-1 mt-2 shrink-0" style={{
        color: "var(--primary)"
      }} aria-hidden="true" />}

        {accent === "underline" && <svg aria-hidden="true" viewBox="0 0 80 8" className="absolute -bottom-1 left-0 w-20 h-2" preserveAspectRatio="none">
            <path d="M2 5 Q 20 1, 40 4 T 78 3" fill="none" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" />
          </svg>}
      </div>

      <p className="font-default italic text-foreground text-lg md:text-xl leading-snug mb-2" style={{
      fontFamily: "var(--font-serif)"
    }}>
        {label}
      </p>

      <p className="font-default text-sm text-muted-foreground leading-relaxed max-w-[18ch]">
        {description}
      </p>
    </div>;
}